/**
 * What the search field and the filter button need to know, read where they stand.
 *
 * They live in the tab strip's own row rather than inside the Ankiety panel, which is where KANON
 * draws them — so they are a sibling of the tab's content, not a child of it, and they cannot take
 * what they need as props from `useSurveysTab`.
 *
 * **This is not a second request.** The two reads below use the SAME query keys the tab's own hook
 * uses, so TanStack serves one cache entry with two observers — the arrangement the plan library's
 * assignment read already stands on. And the component that calls this is rendered only while the
 * Ankiety tab is selected, so the other two tabs still fetch nothing of this tab's.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { clientFacesQueryOptions, surveyTemplatesQueryOptions } from '../api/reportsQueries';
import { clientNames } from '../model/clientFaces';
import { facetClients, type FacetClient } from '../model/surveyFilters';
import { surveyGroups } from '../model/surveyList';

export interface SurveyToolsView {
  /** Every client who actually holds a form — the only people the facet may offer. */
  readonly clients: readonly FacetClient[];
  /** Dead while there is nothing to narrow: a read still open, or one that failed. */
  readonly disabled: boolean;
}

export function useSurveyTools(): SurveyToolsView {
  const { i18n } = useTranslation();
  const templates = useQuery(surveyTemplatesQueryOptions());
  const roster = useQuery(clientFacesQueryOptions());

  const names = clientNames(roster.data ?? new Map());
  return {
    clients: facetClients(surveyGroups(templates.data, i18n.language), names, i18n.language),
    disabled: templates.isPending || templates.isError || roster.isPending || roster.isError,
  };
}
